import {Box, SelectChangeEvent, TextField} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  IQueryFieldOperator,
  getKeywordOperators,
  getNumberOperators,
  QBOperators,
  getTextOperators,
} from '../../../../utils/queryBuilder';
import {
  FieldUIType,
  FieldUITypeMappingObj,
  IFieldModal,
} from '../../field-management/custom-field/constant';
import {QueryFieldDropdown} from './components/QueryFieldDropdown';
import {
  GA_EVENT_NAMES,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';
import {useAnalytics} from '../../../../hooks/useAnalytics';

export interface IQueryFieldGeneralInputParams {
  operator?: QBOperators;
  value?: string;
}

interface IQueryFieldGeneralInputProps {
  selectedField?: IFieldModal;
  initialInputValue?: IQueryFieldGeneralInputParams;
  onChangeFieldParams?: (params: IQueryFieldGeneralInputParams) => void;
  componentName?: string;
}

const QueryFieldGeneralInput: React.FC<IQueryFieldGeneralInputProps> = ({
  selectedField,
  initialInputValue,
  onChangeFieldParams,
  componentName = '',
}) => {
  const fieldUIType =
    FieldUITypeMappingObj[selectedField?.fieldTypeId as FieldUIType];

  const isNumberFieldType = fieldUIType === 'Float' || fieldUIType === 'Number';

  const isKeywordFieldType = fieldUIType === 'Keyword';

  const keywordOrTextOperators: IQueryFieldOperator[] = isKeywordFieldType
    ? getKeywordOperators()
    : getTextOperators();

  const operatorArray: IQueryFieldOperator[] = isNumberFieldType
    ? getNumberOperators()
    : keywordOrTextOperators;

  const [selectedOperator, setSelectedOperator] = useState<
    IQueryFieldGeneralInputParams['operator']
  >(operatorArray[0].name);
  const [fieldValue, setFieldValue] = useState<string>('');
  const {trackGAEvent} = useAnalytics();

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: `${componentName} ${t('gaTracking.queryBuilder.settingsRule')}`,
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, []);

  useEffect(() => {
    if (initialInputValue) {
      setSelectedOperator(initialInputValue.operator);
      setFieldValue(initialInputValue?.value ?? '');
    }
  }, [initialInputValue]);

  const handleConditionChange = useCallback(
    (event: SelectChangeEvent) => {
      const name = operatorArray.find(
        (op) => op.label === event.target.value
      )?.name;
      setSelectedOperator(name);
      onChangeFieldParams?.({
        operator: name,
        value: fieldValue,
      });
    },
    [fieldValue, onChangeFieldParams, operatorArray]
  );

  const handleFieldValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (isNumberFieldType) {
        if (/^\d*(\.\d*)?$/.test(newValue)) {
          setFieldValue(newValue);
          onChangeFieldParams?.({
            operator: selectedOperator,
            value: newValue,
          });
        }
      } else {
        setFieldValue(newValue);
        onChangeFieldParams?.({
          operator: selectedOperator,
          value: newValue,
        });
      }
    },
    [isNumberFieldType, onChangeFieldParams, selectedOperator]
  );

  const disableInput = useMemo(
    () =>
      selectedOperator === QBOperators.isSet ||
      selectedOperator === QBOperators.isNotSet ||
      selectedOperator === QBOperators.isNotSetText ||
      selectedOperator === QBOperators.isSetText,
    [selectedOperator]
  );

  useEffect(() => {
    if (disableInput) {
      setFieldValue('*');
      onChangeFieldParams?.({
        operator: selectedOperator,
        value: '*',
      });
    }
  }, [disableInput, onChangeFieldParams, selectedOperator]);

  return (
    <Box className={'general-input-field-parent-container'}>
      <QueryFieldDropdown
        fieldLabel={selectedField?.fieldLabel}
        selectedOperator={selectedOperator}
        operators={operatorArray}
        handleConditionChange={handleConditionChange}
      />
      <Box>
        <TextField
          variant={'standard'}
          multiline
          className={`query-field-integer-textfield ${disableInput ? 'query-field-integer-textfield-disable' : ''}`}
          value={fieldValue}
          minRows={1}
          maxRows={12}
          placeholder={
            isNumberFieldType
              ? t('indexSearchPage.enterNumber')
              : t('indexSearchPage.enterKeyboard')
          }
          margin={'none'}
          disabled={disableInput}
          inputProps={
            isNumberFieldType
              ? {
                  'data-testid': 'queryTextField',
                  inputMode: 'decimal',
                  pattern: '[0-9]*[.,]?[0-9]*',
                  'aria-label': t('indexSearchPage.enterNumber'),
                }
              : {
                  'data-testid': 'queryTextField',
                  'aria-label': t('indexSearchPage.enterKeyboard'),
                }
          }
          InputProps={{
            disableUnderline: true,
          }}
          onChange={handleFieldValueChange}
        />
      </Box>
    </Box>
  );
};

export {QueryFieldGeneralInput};
